import { EmptyState } from '@/components/EmptyState';
import { PageHeader } from '@/components/PageHeader';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { createDataset, listDatasets } from '@/features/datasets/datasets-api';
import { listMappings } from '@/features/mappings/mappings-api';
import { listTemplates } from '@/features/templates/templates-api';
import { ApiError } from '@/lib/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowUpRight, Database, Loader2, Plus } from 'lucide-react';
import { type ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

export const Route = createFileRoute('/app/datasets/')({
  component: DatasetsPage,
});

const SKELETON_KEYS = ['s1', 's2', 's3', 's4'] as const;

function DatasetsPage(): ReactElement {
  const { t } = useTranslation();
  const datasets = useQuery({ queryKey: ['datasets'], queryFn: listDatasets });
  const [creating, setCreating] = useState(false);
  const list = datasets.data?.datasets ?? [];

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={t('app.layout.section.workspace', { defaultValue: 'Workspace' })}
        title={t('datasets.list.title')}
        description={t('datasets.list.description')}
        actions={
          !creating ? (
            <Button
              onClick={() => setCreating(true)}
              className="bg-foreground text-background hover:opacity-95"
            >
              <Plus className="size-4" aria-hidden />
              {t('datasets.list.newButton')}
            </Button>
          ) : (
            <Badge variant="secondary" className="rounded-full px-3 py-1">
              {t('datasets.create.creatingBadge', { defaultValue: 'Creating dataset' })}
            </Badge>
          )
        }
      />

      {creating ? <CreateDatasetCard onDone={() => setCreating(false)} /> : null}

      <section className="space-y-3">
        {datasets.isLoading ? (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {SKELETON_KEYS.map((k) => (
              <Skeleton key={k} className="h-[88px] rounded-xl" />
            ))}
          </div>
        ) : list.length === 0 ? (
          <EmptyState
            icon={Database}
            title={t('datasets.list.empty')}
            description={t('datasets.list.emptyBody', {
              defaultValue: 'Upload a CSV or hook up a sheet to start mapping data into templates.',
            })}
            action={
              !creating ? (
                <Button onClick={() => setCreating(true)}>
                  <Plus className="size-4" aria-hidden />
                  {t('datasets.list.newButton')}
                </Button>
              ) : null
            }
          />
        ) : (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {list.map((d) => (
              <Link
                key={d.id}
                to="/app/datasets/$id"
                params={{ id: d.id }}
                className="group relative flex items-start gap-3 overflow-hidden rounded-xl border border-border/70 bg-card p-4 shadow-soft transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-elevate"
              >
                <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Database className="size-5" aria-hidden />
                </span>
                <div className="min-w-0 flex-1 space-y-1">
                  <h3 className="truncate text-sm font-semibold tracking-tight">{d.name}</h3>
                  <p className="text-xs text-muted-foreground">
                    {t('datasets.list.rowsCount', { count: d.rowCount })} ·{' '}
                    {t('datasets.list.kb', { size: Math.round(d.sizeBytes / 1024) })}
                  </p>
                </div>
                <ArrowUpRight
                  className="size-4 text-muted-foreground transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-primary"
                  aria-hidden
                />
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function CreateDatasetCard({ onDone }: { onDone: () => void }): ReactElement {
  const { t } = useTranslation();
  const templates = useQuery({
    queryKey: ['templates'],
    queryFn: listTemplates,
  });
  const [templateId, setTemplateId] = useState<string>('');
  const [mappingId, setMappingId] = useState<string>('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const mappings = useQuery({
    queryKey: ['mappings', templateId],
    queryFn: () => listMappings(templateId),
    enabled: !!templateId,
  });

  const create = useMutation({
    mutationFn: () => createDataset({ name, mappingId }),
    onSuccess: (data) => {
      toast.success(t('datasets.create.success'));
      queryClient.invalidateQueries({ queryKey: ['datasets'] });
      void navigate({ to: '/app/datasets/$id', params: { id: data.dataset.id } });
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : t('datasets.create.failed'));
    },
  });

  return (
    <Card className="border-border/70 shadow-soft">
      <CardHeader>
        <div className="flex items-start gap-3">
          <span className="inline-flex size-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Plus className="size-4" aria-hidden />
          </span>
          <div className="space-y-1">
            <CardTitle className="text-base">{t('datasets.create.title')}</CardTitle>
            <CardDescription>{t('datasets.create.description')}</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {error ? (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}
        <div className="grid gap-4 md:grid-cols-3">
          <div className="space-y-1.5">
            <Label htmlFor="ds-name">{t('datasets.create.nameLabel')}</Label>
            <Input id="ds-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ds-template">{t('datasets.create.templateLabel')}</Label>
            <select
              id="ds-template"
              value={templateId}
              onChange={(e) => {
                setTemplateId(e.target.value);
                setMappingId('');
              }}
              className="h-9 w-full rounded-md border bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <option value="">{t('datasets.create.chooseOption')}</option>
              {templates.data?.templates.map((tpl) => (
                <option key={tpl.id} value={tpl.id}>
                  {tpl.name}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ds-mapping">{t('datasets.create.mappingLabel')}</Label>
            <select
              id="ds-mapping"
              value={mappingId}
              onChange={(e) => setMappingId(e.target.value)}
              disabled={!templateId}
              className="h-9 w-full rounded-md border bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-60"
            >
              <option value="">{t('datasets.create.chooseOption')}</option>
              {mappings.data?.mappings.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name} (v{m.version})
                </option>
              ))}
            </select>
            {templateId && mappings.isSuccess && mappings.data.mappings.length === 0 ? (
              <Link
                to="/app/templates/$id/mappings"
                params={{ id: templateId }}
                className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
              >
                {t('datasets.create.noMappingsCta')}
                <ArrowUpRight className="size-3" aria-hidden />
              </Link>
            ) : null}
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => create.mutate()}
            disabled={!name || !mappingId || create.isPending}
          >
            {create.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
            {t('datasets.create.submit')}
          </Button>
          <Button variant="ghost" onClick={onDone} disabled={create.isPending}>
            {t('datasets.create.cancel')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
